import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    TextField,
    IconButton,
    Button,
    Autocomplete,
    MenuItem,
    Alert,
    Box,
    Typography,
    Paper,
    Grid,
    Divider,
    CircularProgress,
    Tooltip,
    Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import axios from 'axios';
import debounce from 'lodash/debounce';
import API_BASE_URL from '../config';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
}));

const AddressCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
}));

const AddressTypeIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
}));

const AddressForm = ({ formData, editMode, setAllAddressDetails }) => {
    const [addresses, setAddresses] = useState([]);
    const [fetchedData, setFetchedData] = useState([]);
    const [selectedVillages, setSelectedVillages] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [villageLoading, setVillageLoading] = useState({});
    const [villageError, setVillageError] = useState({});
    const [apiError, setApiError] = useState(null);
    const [highlightedAddressIndex, setHighlightedAddressIndex] = useState(null);
    const [pincodeError, setPincodeError] = useState(null);
    const [yearErrors, setYearErrors] = useState({});
    const villageSelectRefs = useRef({});
    const pincodeRefs = useRef({});
    const currentYear = new Date().getFullYear();

    const validatePincode = useCallback((pincode) => /^[1-9][0-9]{5}$/.test(pincode), []);

    const fetchPincodeData = useCallback(
        debounce(async (pincode, addressIndex) => {
            if (!validatePincode(pincode)) return;

            setVillageLoading(prev => ({ ...prev, [addressIndex]: true }));
            setVillageError(prev => ({ ...prev, [addressIndex]: null }));
            setPincodeError(null);

            try {
                const sessionResponse = await axios.get(`${API_BASE_URL}/user/id`, {
                    withCredentials: true
                });

                if (!sessionResponse.data || !sessionResponse.data.userId) {
                    throw new Error('User not authenticated');
                }

                const response = await axios.get(`${API_BASE_URL}/fetch-city?pincode=${pincode}`, {
                    withCredentials: true
                });

                if (!response.data || response.data.length === 0) {
                    setVillageError(prev => ({ ...prev, [addressIndex]: 'No villages found for this pincode. Please check and try again.' }));
                    setPincodeError({ index: addressIndex, message: 'No villages found for this pincode. Please check and try again.' });
                    setTimeout(() => {
                        if (pincodeRefs.current[addressIndex]) {
                            pincodeRefs.current[addressIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 300);
                    return;
                }

                setFetchedData((prev) => {
                    const newData = [...prev];
                    newData[addressIndex] = response.data;
                    return newData;
                });
                setHighlightedAddressIndex(addressIndex);
                setTimeout(() => {
                    if (villageSelectRefs.current[addressIndex]) {
                        villageSelectRefs.current[addressIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            } catch (error) {
                console.error('Failed to fetch pincode data:', error);
                let msg = 'Failed to load village data. Please try again.';
                if (error.response?.status === 401) msg = 'Session expired. Please login again.';
                else if (error.response?.status === 404) msg = 'No villages found for this pincode. Please check and try again.';
                else if (error.response?.status === 500) msg = 'Server error. Please try again later.';
                setVillageError(prev => ({ ...prev, [addressIndex]: msg }));
                setPincodeError({ index: addressIndex, message: msg });
                setTimeout(() => {
                    if (pincodeRefs.current[addressIndex]) {
                        pincodeRefs.current[addressIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            } finally {
                setVillageLoading(prev => ({ ...prev, [addressIndex]: false }));
            }
        }, 300),
        [validatePincode]
    );

    const validateYears = (from_year, to_year, index) => {
        const key = `year_${index}`;
        const from = from_year ? parseInt(from_year, 10) : null;
        const to = to_year ? parseInt(to_year, 10) : null;
        if (from && (from < 1950 || from > currentYear)) {
            setYearErrors(prev => ({ ...prev, [key]: 'From Year must be between 1950 and current year.' }));
            return false;
        }
        if (to && (to < 1950 || to > currentYear)) {
            setYearErrors(prev => ({ ...prev, [key]: 'To Year must be between 1950 and current year.' }));
            return false;
        }
        if (from && to && from > to) {
            setYearErrors(prev => ({ ...prev, [key]: 'From Year cannot be greater than To Year.' }));
            return false;
        }
        setYearErrors(prev => ({ ...prev, [key]: null }));
        return true;
    };

    const handleInputChange = (event, index) => {
        const { name, value } = event.target;
        setAddresses((prev) =>
            prev.map((addr, i) => {
                if (i !== index) return addr;

                const updatedAddress = { ...addr, [name]: value };
                if (name === 'pincode') {
                    if (pincodeError && pincodeError.index === index) setPincodeError(null);
                    if (value.length === 6 && validatePincode(value)) {
                        fetchPincodeData(value, index);
                        updatedAddress.isEditingPincode = true;
                    }
                }
                if (name === 'from_year' || name === 'to_year') {
                    const from = name === 'from_year' ? value : addr.from_year;
                    const to = name === 'to_year' ? value : addr.to_year;
                    validateYears(from, to, index);
                }
                return updatedAddress;
            })
        );
    };

    const handleVillageChange = (event, index) => {
        const villageName = event.target.value;
        const village = fetchedData[index]?.find((v) => v.village_name === villageName);

        setSelectedVillages((prev) => {
            const newSelected = [...prev];
            newSelected[index] = village;
            return newSelected;
        });

        if (village) {
            setAddresses((prev) =>
                prev.map((addr, i) =>
                    i === index
                        ? {
                            ...addr,
                            village_name: village.village_name,
                            area: village.subdistrict_name,
                            state: village.state_name,
                            district: village.district_name,
                            subdistrict: village.subdistrict_name,
                        }
                        : addr
                )
            );
        }
    };

    const addressTypeSuggestions = [
        'Current Address',
        'Permanent Address',
        'Office Address',
        'Holiday Home Address',
    ];

    const getAddressTypeIcon = (type) => {
        if (type.includes('Current')) return <HomeIcon />;
        if (type.includes('Permanent')) return <LocationOnIcon />;
        if (type.includes('Office')) return <BusinessIcon />;
        if (type.includes('Holiday')) return <BeachAccessIcon />;
        return <LocationOnIcon />;
    };

    const handleAddAddress = () => {
        const newAddress = {
            id: addresses.length + 1,
            user_id: formData.id || '',
            type: '',
            address: '',
            district: '',
            state: '',
            village_name: '',
            pincode: '',
            subdistrict: '',
            from_year: '',
            to_year: '',
            isEditingPincode: true,
        };
        setAddresses((prev) => [...prev, newAddress]);
        setFetchedData((prev) => [...prev, []]);
        setSelectedVillages((prev) => [...prev, null]);
    };

    const handleRemoveAddress = async (index) => {
        const addressToRemove = addresses[index];

        setAddresses((prev) => prev.filter((_, i) => i !== index));
        setFetchedData((prev) => prev.filter((_, i) => i !== index));
        setSelectedVillages((prev) => prev.filter((_, i) => i !== index));

        try {
            await axios.delete(`${API_BASE_URL}/addresses/${addressToRemove.id}`);
        } catch (error) {
            console.error('Failed to delete address:', error);
            setApiError('Failed to delete address. Please try again.');
            setAddresses((prev) => [...prev.slice(0, index), addressToRemove, ...prev.slice(index)]);
        }
    };

    useEffect(() => {
        const fetchAddresses = async () => {
            if (formData && formData.id) {
                try {
                    // First verify session
                    const sessionResponse = await axios.get(`${API_BASE_URL}/user/id`, {
                        withCredentials: true
                    });

                    if (!sessionResponse.data || !sessionResponse.data.userId) {
                        throw new Error('User not authenticated');
                    }

                    // Then fetch addresses
                    const response = await axios.get(`${API_BASE_URL}/addresses/${formData.id}`, {
                        withCredentials: true
                    });

                    if (response.data && response.data.addresses.length > 0) {
                        // Check if a Primary Address record exists in DB (has real from_year/to_year)
                        const dbPrimary = response.data.addresses.find(
                            addr => addr.type === 'Primary Address'
                        );

                        // Use DB values for from_year/to_year if a primary address record exists
                        const primaryAddress = {
                            id: dbPrimary ? dbPrimary.id : 1,
                            user_id: formData.id,
                            type: 'Primary Address',
                            address: formData.address || '',
                            area: formData.area || '',
                            district: formData.district || '',
                            state: formData.state || '',
                            village_name: formData.village_name || '',
                            pincode: formData.pincode || '',
                            subdistrict: formData.area || '',
                            from_year: dbPrimary ? (dbPrimary.from_year ?? '') : (formData.from_year ?? ''),
                            to_year: dbPrimary ? (dbPrimary.to_year ?? '') : (formData.to_year ?? ''),
                        };

                        const additionalAddresses = response.data.addresses
                            .filter(addr => addr.type !== 'Primary Address')
                            .map(addr => ({
                                ...addr,
                                from_year: addr.from_year ?? '',
                                to_year: addr.to_year ?? '',
                            }));

                        setAddresses([primaryAddress, ...additionalAddresses]);
                    } else {
                        // No addresses in DB — build primary from formData only
                        setAddresses([{
                            id: 1,
                            user_id: formData.id,
                            type: 'Primary Address',
                            address: formData.address || '',
                            area: formData.area || '',
                            district: formData.district || '',
                            state: formData.state || '',
                            village_name: formData.village_name || '',
                            pincode: formData.pincode || '',
                            subdistrict: formData.area || '',
                            from_year: '',
                            to_year: '',
                        }]);
                    }

                } catch (error) {
                    console.error('Error fetching addresses:', error);
                    if (error.response?.status === 401) {
                        console.log('User not authenticated, redirecting to login...');
                    } else if (error.response?.status === 404) {
                        console.log('Addresses not found, setting primary address only');
                        // Set only the primary address from formData
                        setAddresses([{
                            id: 1,
                            user_id: formData.id,
                            type: 'Primary Address',
                            address: formData.address || '',
                            area: formData.area || '',
                            district: formData.district || '',
                            state: formData.state || '',
                            village_name: formData.village_name || '',
                            pincode: formData.pincode || '',
                            subdistrict: formData.area || '',
                            from_year: formData.from_year ?? '',
                            to_year: formData.to_year ?? '',
                        }]);
                    } else {
                        console.error('Error fetching addresses:', error);
                    }
                }
            }
        };

        fetchAddresses();
    }, [formData]);

    useEffect(() => {
        if (setAllAddressDetails && typeof setAllAddressDetails === 'function') {
            setAllAddressDetails(addresses);
        }
    }, [addresses, setAllAddressDetails]);

    const renderAddressFields = (address, index) => {
        const village = selectedVillages[index];
        const isEditingPincode = address.isEditingPincode || false;
        const addressFetchedData = fetchedData[index] || [];
        const isHighlighted = highlightedAddressIndex === index && addressFetchedData.length > 0;
        const hasPincodeError = pincodeError && pincodeError.index === index;
        // console.log('address', formData.city);
        return (
            <AddressCard
                key={index}
                sx={{
                    border: isHighlighted ? (theme) => `2px solid ${theme.palette.primary.main}` :
                        hasPincodeError ? (theme) => `2px solid ${theme.palette.error.main}` : undefined,
                    boxShadow: isHighlighted ? (theme) => `0 0 15px ${alpha(theme.palette.primary.main, 0.3)}` :
                        hasPincodeError ? (theme) => `0 0 15px ${alpha(theme.palette.error.main, 0.3)}` : undefined,
                    transition: 'all 0.3s ease',
                }}
            >
                <Stack direction="row" alignItems="center" mb={2}>
                    <AddressTypeIcon sx={{
                        transition: 'all 0.3s ease',
                        ...(editMode ? {
                            color: (theme) => theme.palette.primary.main,
                            opacity: 1
                        } : {
                            color: 'text.disabled',
                            opacity: 0.5
                        })
                    }}>
                        {getAddressTypeIcon(address.type)}
                    </AddressTypeIcon>
                    <Typography variant="h6" fontWeight="bold" sx={{
                        ...(editMode ? {
                            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                        } : {
                            color: 'text.secondary',
                            opacity: 0.7
                        })
                    }}>
                        {address.type || 'New Address'}
                    </Typography>
                    {editMode && (
                        <Tooltip title="Remove Address">
                            <IconButton
                                onClick={() => handleRemoveAddress(index)}
                                sx={{
                                    ml: 'auto',
                                    color: 'error.main',
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                                    }
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            freeSolo
                            options={addressTypeSuggestions}
                            value={address.type}
                            disabled={!editMode}
                            onInputChange={(event, newValue) => {
                                handleInputChange(
                                    { target: { name: 'type', value: newValue } },
                                    index
                                );
                            }}
                            renderInput={(params) => (
                                <StyledTextField
                                    {...params}
                                    label="Address Type"
                                    required
                                    error={!!errors.type}
                                    helperText={errors.type}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box ref={el => pincodeRefs.current[index] = el}>
                            <StyledTextField
                                required
                                label="Pincode"
                                name="pincode"
                                value={address.pincode}
                                onChange={(e) => handleInputChange(e, index)}
                                disabled={!editMode}
                                fullWidth
                                error={hasPincodeError}
                                inputProps={{
                                    maxLength: 6,
                                    inputMode: 'numeric'
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        ...(hasPincodeError && {
                                            animation: 'shake 0.5s',
                                            '@keyframes shake': {
                                                '0%, 100%': { transform: 'translateX(0)' },
                                                '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                                                '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' }
                                            }
                                        })
                                    }
                                }}
                            />
                            {hasPincodeError && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                        display: 'block',
                                        mt: 1,
                                        fontWeight: 'bold',
                                        animation: 'fadeIn 0.5s',
                                        '@keyframes fadeIn': {
                                            from: { opacity: 0 },
                                            to: { opacity: 1 },
                                        },
                                    }}
                                >
                                    {pincodeError.message}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {address.village_name && !isEditingPincode ? (
                            <StyledTextField
                                label="Village"
                                value={address.village_name}
                                disabled={!editMode}
                                onClick={() =>
                                    setAddresses((prev) =>
                                        prev.map((addr, i) =>
                                            i === index
                                                ? { ...addr, isEditingPincode: true }
                                                : addr
                                        )
                                    )
                                }
                                fullWidth
                            />
                        ) : (
                            <Box ref={el => villageSelectRefs.current[index] = el}>
                                {villageLoading[index] ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">Loading villages...</Typography>
                                    </Box>
                                ) : villageError[index] ? (
                                    <Box>
                                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                                            {villageError[index]}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                setVillageError(prev => ({ ...prev, [index]: null }));
                                                fetchPincodeData(address.pincode, index);
                                            }}
                                        >
                                            Retry
                                        </Button>
                                    </Box>
                                ) : addressFetchedData.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No villages found
                                    </Typography>
                                ) : (
                                    <StyledTextField
                                        select
                                        label="Select Village"
                                        value={village?.village_name || ''}
                                        onChange={(e) => {
                                            handleVillageChange(e, index);
                                            setHighlightedAddressIndex(null);
                                        }}
                                        fullWidth
                                        disabled={!editMode}
                                        sx={{
                                            '& .MuiOutlinedInput-root': isHighlighted ? {
                                                animation: 'pulse 1.5s infinite',
                                                '@keyframes pulse': {
                                                    '0%': { boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}` },
                                                    '70%': { boxShadow: (theme) => `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}` },
                                                    '100%': { boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
                                                },
                                            } : {},
                                        }}
                                    >
                                        {addressFetchedData.map((item, i) => (
                                            <MenuItem key={i} value={item.village_name}>
                                                {item.village_name}
                                            </MenuItem>
                                        ))}
                                    </StyledTextField>
                                )}
                                {isHighlighted && !villageLoading[index] && !villageError[index] && addressFetchedData.length > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            fontWeight: 'bold',
                                            animation: 'fadeIn 0.5s',
                                            '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                                        }}
                                    >
                                        Please select a village from the list
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            required
                            label="House No./Plot No./Sector"
                            name="address"
                            value={address.address}
                            onChange={(e) => handleInputChange(e, index)}
                            error={!!errors.address}
                            helperText={errors.address}
                            disabled={!editMode}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            label="Area/Subdistrict"
                            name="area"
                            value={address.area}
                            disabled
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            label="District"
                            name="district"
                            value={address.district}
                            disabled
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            label="State"
                            name="state"
                            value={address.state}
                            disabled
                            fullWidth
                        />
                    </Grid>

                    {/* From Year */}
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            label="From Year"
                            name="from_year"
                            type="number"
                            value={address.from_year || ''}
                            onChange={(e) => handleInputChange(e, index)}
                            disabled={!editMode}
                            fullWidth
                            placeholder="Enter start year"
                            helperText={
                                yearErrors[`year_${index}`]
                                    ? yearErrors[`year_${index}`]
                                    : 'Optional - helps indicate duration of stay'
                            }
                            error={!!yearErrors[`year_${index}`]}
                            inputProps={{ min: 1950, max: currentYear }}
                        />
                    </Grid>

                    {/* To Year */}
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledTextField
                            label="To Year"
                            name="to_year"
                            type="number"
                            value={address.to_year || ''}
                            onChange={(e) => handleInputChange(e, index)}
                            disabled={!editMode}
                            fullWidth
                            placeholder="Enter end year"
                            helperText={
                                yearErrors[`year_${index}`]
                                    ? ''
                                    : 'Optional - helps indicate duration of stay'
                            }
                            error={!!yearErrors[`year_${index}`]}
                            inputProps={{ min: 1950, max: currentYear }}
                        />
                    </Grid>

                    {/* Stay Duration Display */}
                    {(address.from_year || address.to_year) && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Stay Duration:{' '}
                                {address.from_year && address.to_year
                                    ? `${address.from_year} - ${address.to_year}`
                                    : address.from_year
                                    ? `From ${address.from_year}`
                                    : `Till ${address.to_year}`}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </AddressCard>
        );
    };

    if (apiError) {
        return <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>;
    }

    return (
        <Box>
            {addresses.length === 0 && !editMode ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No addresses added yet. Enable edit mode to add addresses.
                </Typography>
            ) : (
                addresses.map((address, index) => renderAddressFields(address, index))
            )}
            {editMode && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <ActionButton
                        variant="contained"
                        onClick={handleAddAddress}
                        startIcon={<AddIcon />}
                    >
                        Add Address
                    </ActionButton>
                </Box>
            )}
        </Box>
    );
};

export default AddressForm;
